<template>
  <div>
    <profile-section-header title="Professional Summary" />

    <ProfileSubSectionHeader title="Experience" icon="experience-icon" />
    <ul class="profile-status-list">
      <GfbOptionDisplay
        :status="
          booleanToOptionStatus(profile.jobSeeker.hasManagementExperience)
        "
        text="Has Project or Jobsite Management Experience"
      />
    </ul>
    <capsule-list
      v-if="
        profile.jobSeeker.hasManagementExperience &&
        profile.jobSeeker.managementExperienceCategories &&
        profile.jobSeeker.managementExperienceCategories.length
      "
      class="mb-3"
      :items="profile.jobSeeker.managementExperienceCategories"
      :highlighted="true"
    />

    <ul class="profile-status-list">
      <GfbOptionDisplay
        :status="booleanToOptionStatus(profile.jobSeeker.hasCcsCard)"
        text="Has an active Coalition of Construction Safety (CCS) card"
      />
      <GfbOptionDisplay
        v-if="
          profile.jobSeeker.oshaCertification &&
          profile.jobSeeker.oshaCertification !== 'No'
        "
        status="success"
        :text="`Holds an OSHA certification (${profile.jobSeeker.oshaCertification})`"
      />
      <GfbOptionDisplay
        v-else-if="profile.jobSeeker.oshaCertification === 'No'"
        status="fail"
        text="Holds an OSHA certification"
      />
      <GfbOptionDisplay
        v-else
        status="missing"
        text="Holds an OSHA certification"
      />
    </ul>

    <ProfileSubSectionHeader
      title="Additional Certifications, Licenses, or Skills"
      icon="certifications-icon"
    />
    <div
      v-if="profile.jobSeeker.idealJobDescription"
      class="additional-certs"
      v-html="profile.jobSeeker.idealJobDescription"
    />
    <p v-else class="no-experience">None provided</p>

    <div class="d-flex flex-wrap align-items-center mb-2">
      <ProfileSubSectionHeader title="Looking For" icon="looking-for-icon" />
      <capsule-list
        v-if="profile.jobSeeker.positionSeeking?.length"
        :items="profile.jobSeeker.positionSeeking"
        :highlighted="true"
      />
      <p v-else class="no-experience mb-0">None provided</p>
    </div>
    <!-- <capsule-list class="mt-4 mb-4" :items="experienceNames" /> -->
    <ProfileSubSectionHeader
      title="Trade Experience & Skills"
      icon="trade-experience-icon"
      :showEdit="false"
      @toggle-edit="toggleEdit"
      :isEditing="editSkills"
      @save="handleSave"
      @add-new="handleAddNew()"
      addNewLabel="Add New Skill"
    />
    <div class="row">
      <ul class="experience">
        <li
          v-for="(experience, index) in skillExperiences"
          :key="experience.category"
        >
          <label>{{ experience.category }}</label>
          <div class="experience-level-wrapper">
            <ExperienceSliderContainer
              :rating="experience.rating"
              @on-change="(rating) => updateRating(index, rating)"
              :isEditing="editSkills"
            />
            <span v-if="experience.amount" class="years-experience"
              >Years Experience: {{ experience.amount }}</span
            >
          </div>
          <div
            v-if="editSkills"
            class="delete-btn"
            @click="deleteExperience(experience.category)"
          >
            <PhTrash color="#525E77" :size="20" />
            Remove
          </div>
        </li>
      </ul>
    </div>
    <p v-if="!skillExperiences.length" class="no-experience">None provided</p>
    <div
      v-if="
        profile.jobSeeker.handsOnExperiencesTrainings &&
        profile.jobSeeker.handsOnExperiencesTrainings.length
      "
      class="d-flex flex-wrap align-items-center mb-2"
    >
      <ProfileSubSectionHeader
        title="Interested in These Trade Categories"
        icon="trade-experience-icon"
      />
      <capsule-list
        :items="profile.jobSeeker.handsOnExperiencesTrainings"
        :highlighted="true"
      />
    </div>

    <default-modal
      description="Please select the skills you want to add"
      title="Add New Skill"
      @onConfirm="handleModalConfirm()"
      id="add-experience-modal"
      :showCancel="true"
    >
      <template v-slot:input>
        <CustomMultiSelect
          :selectOptions="categoryOptions"
          :selectedList="handsOnExperienceCategories"
          selectLabel="Search or select category"
          @update-selectde="updateExperiences"
        ></CustomMultiSelect>
      </template>
    </default-modal>
  </div>
</template>

<script>
import ProfileSectionHeader from "../ProfileSectionHeader.vue"
import ProfileSubSectionHeader from "../ProfileSubSectionHeader.vue"
import ExperienceSliderContainer from "@/components/ExperienceSliderContainer"
import { PhTrash } from "@phosphor-icons/vue"
import CustomMultiSelect from "@/components/CustomMultiSelect"
import GfbOptionDisplay from "@/components/GfbOptionDisplay"
import { booleanToOptionStatus } from "@/utils/converters"

export default {
  name: "candidate-profile-experience",
  components: {
    ProfileSectionHeader,
    ProfileSubSectionHeader,
    ExperienceSliderContainer,
    PhTrash,
    CustomMultiSelect,
    GfbOptionDisplay,
  },
  props: {
    profile: {
      type: Object,
      required: true,
    },
    experienceDescription: String,
    experiences: {
      type: Array,
      required: true,
      default: () => [],
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editSkills: false,
      skillExperiences: [],
      tempExperiences: [],
    }
  },
  created() {
    this.skillExperiences = JSON.parse(JSON.stringify(this.experiences))
  },
  methods: {
    booleanToOptionStatus,
    updateRating(index, rating) {
      this.skillExperiences[index].rating = Number(rating)
      this.skillExperiences = [...this.skillExperiences]
    },
    deleteExperience(category) {
      this.skillExperiences = this.skillExperiences.filter(
        (experience) => experience.category !== category
      )
    },
    handleSave() {
      this.$emit("on-experiences-change", this.skillExperiences)
      this.editSkills = false
    },
    toggleEdit() {
      this.skillExperiences = JSON.parse(JSON.stringify(this.experiences))
      this.editSkills = !this.editSkills
    },
    selectExperience(option) {
      let experiences = [...this.tempExperiences]
      experiences.push({
        category: option,
        amount: "1-5YR",
      })
      this.tempExperiences = [...experiences]
    },
    removeExperience(option) {
      this.tempExperiences = this.tempExperiences.filter(
        (experience) => experience.category !== option
      )
    },
    updateExperiences(selected) {
      this.tempExperiences = selected
    },
    handleAddNew() {
      this.tempExperiences = JSON.parse(JSON.stringify(this.skillExperiences))
      this.$bvModal.show("add-experience-modal")
    },
    handleModalConfirm() {
      this.skillExperiences = JSON.parse(JSON.stringify(this.tempExperiences))
      this.$bvModal.hide("add-experience-modal")
      this.tempExperiences = []
    },
  },
  computed: {
    categoryOptions() {
      return this.$store.state.categories.map((c) => c.name)
    },
    experienceNames() {
      const experienceNames = this.experiences.map(
        (experience) => experience.category
      )
      return experienceNames
    },
    handsOnExperienceCategories() {
      return this.tempExperiences.map((experience) => experience.category)
    },
  },
  watch: {
    experiences(newVal) {
      this.skillExperiences = [...newVal]
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-status-list {
  width: 100%;
  list-style: none;
  padding: 0;
  margin-top: 12px;

  li {
    margin-bottom: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;

    svg {
      margin-right: 8px;
      flex-shrink: 0;
    }
  }
}

.no-experience {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  text-align: left;
}

.additional-certs {
  font-size: 14px;
  color: var(--Gray-700, #525e77);
  margin-bottom: 20px;
}

.experience-level-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.years-experience {
  font-size: 13px;
  color: var(--Gray-500, #677690);
  font-weight: 500;
}

.experience {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  li {
    display: flex;
    margin-bottom: 25px;
  }

  label {
    width: 35%;
    font-weight: 600;
    color: #3a4252;
  }

  .delete-btn {
    padding: 6px 12px 6px 8px;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    border: 1px solid var(--Gray-300, #b0b9c9);
    font-size: 14px;
    margin-left: 20px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
@media (max-width: 768px) {
  li {
    flex-direction: column;
    gap: 12px;
  }

  label {
    width: 100% !important;
  }
}
</style>
